package no.vareflow.web

import no.vareflow.app.domain.*

/**
 * Time-limited helpers ("Hjelper") in the cloud: an Ansatt with `accessUntil` on the membership.
 * The admin opens, extends or closes the time here and shows the login QR code / link. The QR code
 * holds only the Firmakode and the brukernavn – the start code is given separately.
 */

/** What the admin needs to hand a new helper. */
data class HelperLogin(val companyCode: String, val username: String, val startCode: String?, val accessUntil: Long?)

/** Duration chips (2 t, 4 t, 8 t, 1 dag, 3 dager, 1 uke). */
fun El.helperDurationChips(selectedHours: Int, onSelect: (Int) -> Unit) {
    div("chip-row wrap") {
        HelperAccess.PRESET_HOURS.forEach { hours ->
            chip(HelperAccess.presetLabel(hours), hours == selectedHours) { onSelect(hours) }
        }
    }
}

/** "Hjelper · tilgang til 14.10 kl. 18:00" / "Hjelper · tilgangen er utløpt" (null when no limit). */
fun helperStatusText(accessUntil: Long?, now: Long = nowMillis()): String? = when {
    accessUntil == null -> null
    HelperAccess.isExpired(accessUntil, now) -> t("Hjelper · tilgangen er utløpt", "Helper · access has expired")
    else -> t("Hjelper · tilgang til ${formatDateTime(accessUntil)} (${HelperAccess.remainingText(accessUntil, now)} igjen)",
        "Helper · access until ${formatDateTime(accessUntil)} (${HelperAccess.remainingText(accessUntil, now)} left)")
}

/**
 * The login card for a helper: QR code, link (with a copy button) and the lines to read out.
 * [startCode] is only known right after the person was created.
 */
fun WebApp.helperLoginCard(parent: El, login: HelperLogin) {
    val app = this
    val link = HelperAccess.loginLink(login.companyCode, login.username)
    parent.div("helper-card") {
        attr("role", "group")
        attr("aria-label", t("Innlogging for hjelper", "Login for helper"))
        // Generated from the Firmakode (A–Z, 2–9) and brukernavn (a–z, 0–9, . - _) only: safe markup.
        svg(QrCode.encode(link).toSvg(), "helper-qr").attr("aria-label", t("QR-kode til innloggingen", "QR code for the login")).attr("role", "img")
        div("helper-lines") {
            div("small", t("Skann QR-koden med mobilkameraet, eller åpne lenken:", "Scan the QR code with the phone camera, or open the link:"))
            div("helper-link", link)
            button(t("Kopier lenke", "Copy link"), "btn outline small") {
                copyToClipboard(link) { ok -> app.toast(if (ok) t("Lenken er kopiert.", "The link is copied.") else t("Kunne ikke kopiere. Merk lenken og kopier selv.", "Could not copy. Select the link and copy it yourself.")) }
            }
            div("helper-facts") {
                detailRow(t("Firmakode", "Company code"), login.companyCode)
                detailRow(t("Brukernavn", "Username"), login.username)
                if (login.startCode != null) detailRow(t("Startkode", "Start code"), login.startCode)
                login.accessUntil?.let { detailRow(t("Tilgang til", "Access until"), formatDateTime(it)) }
            }
            if (login.startCode != null) {
                div("muted tiny", t("Startkoden er passordet. Den vises bare nå og står ikke i QR-koden – gi den muntlig eller på SMS.",
                    "The start code is the password. It is shown only now and is not in the QR code – give it in person or by text message."))
            } else {
                div("muted tiny", t("Startkoden ble vist da personen ble lagt til. Den står ikke i QR-koden.",
                    "The start code was shown when the person was added. It is not in the QR code."))
            }
        }
    }
}

private fun copyToClipboard(text: String, done: (Boolean) -> Unit) {
    try {
        val clipboard = window.navigator.clipboard
        if (clipboard == null || clipboard == undefined) return done(false)
        clipboard.writeText(text).then({ _: dynamic -> done(true) }, { _: dynamic -> done(false) })
    } catch (e: Throwable) {
        done(false)
    }
}

/**
 * Brukere og tilgang → an Ansatt → "Tilgang og QR": open for a while, extend, close now,
 * or make the person a permanent employee again.
 */
fun WebApp.showHelperAccessDialog(personId: String, admin: UserAccount) {
    var hours = HelperAccess.DEFAULT_HOURS
    openDialog(t("Tidsbegrenset tilgang", "Time-limited access")) { close ->
        val person = core.userById(personId)
        if (person == null) { close(); return@openDialog }
        val now = nowMillis()
        h2(person.name, "dialog-subtitle")
        val until = person.accessUntil
        p(when {
            until == null -> t("${person.name} er fast ansatt uten tidsgrense. Gjør personen til hjelper ved å velge hvor lenge tilgangen skal vare.",
                "${person.name} is a permanent employee without a time limit. Make the person a helper by choosing how long access should last.")
            HelperAccess.isExpired(until, now) -> t("Tilgangen er utløpt (${formatDateTime(until)}). ${person.name} kan ikke logge inn.",
                "Access has expired (${formatDateTime(until)}). ${person.name} cannot log in.")
            else -> t("${person.name} kan jobbe til ${formatDateTime(until)} (${HelperAccess.remainingText(until, now)} igjen).",
                "${person.name} can work until ${formatDateTime(until)} (${HelperAccess.remainingText(until, now)} left).")
        }, "small")
        div("strong small", if (until != null && !HelperAccess.isExpired(until, now)) t("Forleng: åpen i", "Extend: open for") else t("Åpne i", "Open for"))
        helperDurationChips(hours) { hours = it; requestRender() }
        div("button-row") {
            button(t("Åpne ${HelperAccess.presetLabel(hours)} fra nå", "Open ${HelperAccess.presetLabel(hours)} from now"), "btn primary", enabled = person.active) {
                core.updateUser(person.copy(accessUntil = HelperAccess.until(nowMillis(), hours)), admin)
                toast(t("${person.name} har tilgang til ${formatDateTime(HelperAccess.until(nowMillis(), hours))}.", "${person.name} has access until ${formatDateTime(HelperAccess.until(nowMillis(), hours))}."))
            }
            if (until != null && !HelperAccess.isExpired(until, now)) button(t("Steng nå", "Close now"), "btn outline") {
                core.updateUser(person.copy(accessUntil = nowMillis()), admin)
                toast(t("Tilgangen til ${person.name} er stengt.", "${person.name}'s access is closed."))
            }
        }
        if (until != null) button(t("Fjern tidsgrensen (fast ansatt)", "Remove the time limit (permanent employee)"), "btn text") {
            core.updateUser(person.copy(accessUntil = null), admin)
            toast(t("${person.name} er nå fast ansatt.", "${person.name} is now a permanent employee."))
        }
        val code = cloud.membership?.companyCode.orEmpty()
        if (CompanyLogin.isSyntheticEmail(person.username) && code.isNotBlank()) {
            div("strong small", t("Innlogging", "Login"))
            helperLoginCard(this, HelperLogin(code, CompanyLogin.displayLogin(person.username), null, person.accessUntil))
        } else if (!CompanyLogin.isSyntheticEmail(person.username)) {
            p(t("${person.name} logger inn med e-post (${person.username}).", "${person.name} logs in with e-mail (${person.username})."), "muted tiny")
        }
        dialogActions { button(t("Lukk", "Close"), "btn text") { close() } }
    }
}
